export const RECENT_GAMES_LIMIT = 10;
export const RECENT_GAMES_STORAGE_KEY = "dreamsport_recent_games";

/** Load user recents from localStorage (frontend-only, not backend). */
export const loadRecentGamesFromStorage = () => {
  try {
    const raw = localStorage.getItem(RECENT_GAMES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((g) => g?.id) : [];
  } catch {
    return [];
  }
};

/** Persist recents to localStorage. */
export const saveRecentGamesToStorage = (recentGames = []) => {
  try {
    localStorage.setItem(
      RECENT_GAMES_STORAGE_KEY,
      JSON.stringify(Array.isArray(recentGames) ? recentGames : [])
    );
  } catch {
    // ignore quota / private mode errors
  }
};

/** Remove all recents from localStorage. */
export const clearRecentGamesFromStorage = () => {
  try {
    localStorage.removeItem(RECENT_GAMES_STORAGE_KEY);
  } catch {
    // ignore private mode errors
  }
};

/** Remove a single match from recents by id. */
export const removeRecentGame = (recentGames = [], gameId) => {
  if (!gameId) return Array.isArray(recentGames) ? recentGames : [];
  return (Array.isArray(recentGames) ? recentGames : []).filter(
    (g) => g.id !== gameId
  );
};

/** Add or bump a match to the front of recents (deduped by id). */
export const addRecentGame = (
  recentGames = [],
  game,
  limit = RECENT_GAMES_LIMIT
) => {
  if (!game?.id) return recentGames;
  const list = Array.isArray(recentGames) ? recentGames : [];
  const without = list.filter((g) => g.id !== game.id);
  return [game, ...without].slice(0, limit);
};

/** Refresh stored recents with latest odds/details from the live feed when available. */
export const hydrateRecentGames = (recentGames = [], gameLiveData = []) => {
  if (!Array.isArray(recentGames) || recentGames.length === 0) return [];
  if (!Array.isArray(gameLiveData) || gameLiveData.length === 0) {
    return recentGames;
  }

  const liveById = new Map(
    gameLiveData.filter((g) => g?.id).map((g) => [g.id, g])
  );

  return recentGames.map((stored) => liveById.get(stored.id) || stored);
};

/** Split favorites and recents so both sections can render together. */
export const partitionFavoritesRecents = (favGames = [], recentGames = []) => {
  const favorites = Array.isArray(favGames) ? favGames : [];
  const favIds = new Set(favorites.map((g) => g?.id).filter(Boolean));
  const recents = (Array.isArray(recentGames) ? recentGames : []).filter(
    (g) => g?.id && !favIds.has(g.id)
  );
  return { favorites, recents };
};

export const hasFavoritesRecentsContent = (favorites = [], recents = []) =>
  favorites.length > 0 || recents.length > 0;

export const getGameCardElementId = (gameId) =>
  gameId ? `game-card-${gameId}` : "";

/** Find the freshest copy of a match from the live odds feed. */
export const resolveGameFromFeed = (games = [], item) => {
  if (!item?.id || !Array.isArray(games)) return item || null;
  return games.find((g) => g?.id === item.id) || item;
};

/** Index of a game in the currently visible center-panel list (-1 if missing). */
export const findGameIndexInList = (games = [], gameId) => {
  if (!gameId || !Array.isArray(games)) return -1;
  return games.findIndex((g) => g?.id === gameId);
};

export const BOARD_FILTER_FAVORITES = "favorites";
export const BOARD_FILTER_QUERY_KEY = "filter";

/** True when the board URL asks for the Favorites filter. */
export const isFavoritesBoardFilter = (search = "") => {
  try {
    const params = new URLSearchParams(
      typeof search === "string" ? search : ""
    );
    return params.get(BOARD_FILTER_QUERY_KEY) === BOARD_FILTER_FAVORITES;
  } catch {
    return false;
  }
};

/**
 * Join starred snapshots to the live board feed.
 * Only matches still on the odds board are playable Favorites; ended /
 * delisted ids are returned as unavailable for callers that need the split,
 * but the Favorites filter itself shows playable only.
 */
export const partitionFavoriteBoardGames = (
  favGames = [],
  liveGames = []
) => {
  const favorites = Array.isArray(favGames) ? favGames.filter((g) => g?.id) : [];
  const liveById = new Map(
    (Array.isArray(liveGames) ? liveGames : [])
      .filter((g) => g?.id)
      .map((g) => [g.id, g])
  );

  const playable = [];
  const unavailable = [];

  for (const fav of favorites) {
    const live = liveById.get(fav.id);
    if (live) playable.push(live);
    else unavailable.push(fav);
  }

  return { playable, unavailable };
};

/** True when the Favorites league chip is the default "All". */
export const isFavoritesLeagueAll = (selectedGameKey) => {
  const key = selectedGameKey?.key || selectedGameKey?.title || selectedGameKey?.tittle;
  return !key || key === "All";
};

/**
 * Narrow playable favorites to one league chip without refetching odds.
 * League chips on Favorites are client-side filters of the all-sports feed.
 */
export const filterFavoriteGamesByLeague = (games = [], leagueKey) => {
  const list = Array.isArray(games) ? games : [];
  if (!leagueKey || leagueKey === "All") return list;
  return list.filter((game) => game?.sport_key === leagueKey);
};
